import { organizationEndpoints } from '@/api/endpoints/organizations';
import { apiFetch } from '@/util/apiClient';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export interface UserPreferences {
  pinnedProjects: string[];
  pinnedSubprojects: string[];
}

export function useUserPreferences(orgId?: string) {
  return useQuery({
    queryKey: ['organization', orgId, 'preferences'],
    queryFn: async (): Promise<UserPreferences> => {
      if (!orgId) {
        return { pinnedProjects: [], pinnedSubprojects: [] };
      }
      return apiFetch<UserPreferences>(
        organizationEndpoints.PREFERENCES(orgId),
      );
    },
    enabled: Boolean(orgId),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    placeholderData: { pinnedProjects: [], pinnedSubprojects: [] }, // Default data while loading
  });
}

export function useUpdateUserPreferences(orgId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (preferences: Partial<UserPreferences>) => {
      if (!orgId) {
        throw new Error('No organization selected');
      }
      return apiFetch<UserPreferences>(
        organizationEndpoints.PREFERENCES(orgId),
        {
          method: 'PATCH',
          body: JSON.stringify(preferences),
        },
      );
    },
    // Optimistically update the cache before the server responds
    onMutate: async (newPrefs) => {
      if (!orgId) return;

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ['organization', orgId, 'preferences'],
      });

      // Snapshot the previous value
      const previousPrefs = queryClient.getQueryData<UserPreferences>([
        'organization',
        orgId,
        'preferences',
      ]);

      // Optimistically update to the new value
      const optimisticValue = {
        pinnedProjects:
          newPrefs.pinnedProjects ?? previousPrefs?.pinnedProjects ?? [],
        pinnedSubprojects:
          newPrefs.pinnedSubprojects ?? previousPrefs?.pinnedSubprojects ?? [],
      };

      queryClient.setQueryData<UserPreferences>(
        ['organization', orgId, 'preferences'],
        optimisticValue,
      );

      // Return a context object with the snapshotted value
      return { previousPrefs };
    },
    // If the mutation succeeds, update cache with server response
    onSuccess: (data) => {
      if (orgId) {
        queryClient.setQueryData(['organization', orgId, 'preferences'], data);
      }
    },
    // If the mutation fails, rollback to the previous value
    onError: (_err, _newPrefs, context) => {
      if (orgId && context?.previousPrefs) {
        queryClient.setQueryData(
          ['organization', orgId, 'preferences'],
          context.previousPrefs,
        );
      }
    },
  });
}
